import type { FC } from 'react';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { token } from '@atlaskit/tokens';
import Image from '@atlaskit/image';
import Heading from '@atlaskit/heading';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { ConfluenceLabsLozenge } from '@confluence/ai-common';

import AudioBriefingChangeboardingIllustration from '../../assets/AudioBriefingChangeboardingIllustration.svg';
import AudioBriefingChangeboardingIllustrationDark from '../../assets/AudioBriefingChangeboardingIllustrationDark.svg';

import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	buttonWrapper: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.150'),
	},
	audioBriefingIllustrationContainer: {
		paddingBottom: token('space.250'),
	},
	audioBriefingIllustration: {
		width: '81px',
		height: '62px',
	},
	listenButtonContainer: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.150'),
	},
	header: {
		marginBottom: token('space.075'),
	},
});

const i18n = defineMessages({
	audioBriefingChangeboardingHeader: {
		id: 'audio.briefing-popup.changeboarding-header',
		description: 'Header for introducing the audio briefings feature',
		defaultMessage: 'Introducing audio briefings',
	},
	audioBriefingChangeboardingBody: {
		id: 'audio.briefing-popup.changeboarding-body',
		description: 'Description for audio briefings',
		defaultMessage:
			'Briefings are overviews that help you get up to speed on everything relating to this page.',
	},
	audioBriefingChangeboardingListenButton: {
		id: 'audio.briefing-popup.changeboarding.button.listen',
		description: 'Text for button that allows users to acknowledge audio briefing changeboard',
		defaultMessage: 'Listen now',
	},
});

type AudioBriefingChangeboardingProps = {
	closePopup: () => void;
};

export const AudioBriefingChangeboarding: FC<AudioBriefingChangeboardingProps> = ({
	closePopup,
}) => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const onClick = async () => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
			value: 'true',
		});
	};

	return (
		<Box xcss={styles.wrapper} padding="space.200">
			<Inline spread="space-between" alignBlock="center" xcss={styles.header}>
				<ConfluenceLabsLozenge />
				<AudioBriefingPopupControlButton closePopup={closePopup} />
			</Inline>
			<Stack alignInline="center">
				<Box xcss={styles.audioBriefingIllustrationContainer}>
					<Inline xcss={styles.audioBriefingIllustration} alignInline="center" alignBlock="center">
						<Image
							src={AudioBriefingChangeboardingIllustration}
							srcDark={AudioBriefingChangeboardingIllustrationDark}
							alt=""
						/>
					</Inline>
				</Box>

				<Stack space="space.050" alignInline="center">
					<Heading size="small">{formatMessage(i18n.audioBriefingChangeboardingHeader)}</Heading>
					<Text color="color.text.subtle" align="center" size="medium">
						{formatMessage(i18n.audioBriefingChangeboardingBody)}
					</Text>
				</Stack>

				<Inline alignInline="center" xcss={styles.buttonWrapper}>
					<Button
						iconBefore={(iconProps) => (
							<VideoPlayOverlayIcon {...iconProps} color={token('color.icon.selected')} />
						)}
						onClick={onClick}
					>
						<Text>{formatMessage(i18n.audioBriefingChangeboardingListenButton)}</Text>
					</Button>
				</Inline>
			</Stack>
		</Box>
	);
};
